import { Link, useNavigate } from "@remix-run/react";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { IconStar } from "@tabler/icons-react";
import { Button } from "~/components/ui/button";

type ProjectCardProps = {
  name: string;
  description: string;
  stargazersCount: number;
  ownerLogin: string;
  ownerAvatarUrl: string;
  openGraphImageUrl?: string;
  variant?: "default" | "small";
  children?: React.ReactNode;
  baseProjectName?: string;
  baseProjectOwner?: string;
};

export function ProjectCard({
  name,
  description,
  stargazersCount,
  ownerLogin,
  ownerAvatarUrl,
  openGraphImageUrl,
  variant = "default",
  children,
  baseProjectName,
  baseProjectOwner,
}: ProjectCardProps) {
  const navigate = useNavigate();

  if (stargazersCount < 1000) {
    return null;
  }

  const capitalizedBaseProjectName = baseProjectName
    ? baseProjectName.charAt(0).toUpperCase() + baseProjectName.slice(1)
    : null;

  const cardContent = (
    <>
      <CardHeader className="relative">
        <div
          className={`flex ${
            variant === "small" ? "flex-col" : "justify-between"
          } items-start`}
        >
          <div className="flex items-center space-x-4">
            <img
              src={ownerAvatarUrl}
              alt={`${ownerLogin}'s avatar`}
              className={`rounded-full ${
                variant === "small" ? "w-8 h-8" : "w-10 h-10"
              }`}
            />
            <CardTitle
              className={variant === "small" ? "text-base" : "text-lg"}
            >
              {baseProjectName ? (
                <Link
                  to={`/library/${ownerLogin}/${name}`}
                  className="hover:underline"
                >
                  {name}
                </Link>
              ) : (
                name
              )}
            </CardTitle>
          </div>
          <span
            className={`text-sm font-medium flex items-center ${
              variant === "small" ? "mt-2" : ""
            }`}
          >
            <IconStar size={16} className="mr-1 text-yellow-500" />
            {stargazersCount.toLocaleString()}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex-grow flex flex-col">
        <p
          className={`text-sm text-muted-foreground ${
            variant === "small" ? "line-clamp-2" : "line-clamp-6"
          }`}
        >
          {description}
        </p>
        {openGraphImageUrl && variant !== "small" && (
          <img
            src={openGraphImageUrl}
            alt={`${name} preview`}
            className="mt-4 w-full h-auto rounded-md object-cover"
            style={{ maxHeight: "150px" }}
          />
        )}
        <div className="mt-auto flex justify-start">
          {baseProjectName && baseProjectOwner && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4 -mb-1.5"
              onClick={(e) => {
                const url = `/compare/${baseProjectOwner}>${baseProjectName}/${ownerLogin}>${name}`;
                if (e.shiftKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            >
              Compare to {capitalizedBaseProjectName}
            </Button>
          )}
          {children}
        </div>
      </CardContent>
    </>
  );

  const cardClasses = `h-full flex flex-col overflow-hidden ${
    variant === "small" ? "" : ""
  } ${
    !baseProjectName
      ? "transition-all duration-300 hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg"
      : ""
  }`;

  return baseProjectName ? (
    <Card className={cardClasses}>{cardContent}</Card>
  ) : (
    <Link to={`/library/${ownerLogin}/${name}`} className="block">
      <Card className={cardClasses}>{cardContent}</Card>
    </Link>
  );
}
